/**
 * ReadyButton — emits `Ready { playerId }` (LOCK, D9) (ui/31 §2/§3).
 * `<GameView>` computes `disabled` (not in planning phase, already ready, or
 * `canApply` rejects it — ui/31 §3 affordance gating) and `waiting` (the viewer
 * is ready and the opponent is not) and passes them in; this component holds
 * no logic of its own.
 */
import type { ReactElement } from 'react';
import { Button } from './kit/index.js';

export interface ReadyButtonProps {
  disabled: boolean;
  /**
   * The viewer pressed "Готов" and the round waits for the opponent. The
   * engine has no un-ready action, so this state is disabled too (design
   * system 02 §5.2, Q8).
   */
  waiting?: boolean;
  /**
   * «Пора»: the plan is not empty, or nothing left in hand can be played. The
   * desktop button then nudges its step every 2.4s (02 §5.2); CSS owns when.
   */
  nudge?: boolean;
  onClick: () => void;
}

export function ReadyButton({ disabled, waiting = false, nudge = false, onClick }: ReadyButtonProps): ReactElement {
  return (
    <Button
      type="button"
      variant="primary"
      size="md"
      className="cards-ready-button"
      data-testid="ready-button"
      data-onboarding="ready"
      data-waiting={waiting ? 'true' : undefined}
      data-nudge={nudge && !disabled && !waiting ? 'true' : undefined}
      disabled={disabled || waiting}
      onClick={onClick}
    >
      {waiting ? (
        <>
          Ждём
          <span className="cards-ready-button-dots" aria-hidden="true"><span /><span /><span /></span>
        </>
      ) : 'Готов'}
    </Button>
  );
}
